/// FOURJS_START_COPYRIGHT(D,2018)
/// Property of Four Js*
/// (c) Copyright Four Js 2018, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("SplitLayoutEngine", ["LayoutEngineBase"], function (context, cls) {
  /**
   * @class SplitLayoutEngine
   * @memberOf classes
   * @extends classes.LayoutEngineBase
   */
  cls.SplitLayoutEngine = context.oo.Class(cls.LayoutEngineBase, function ($super) {
    return /** @lends classes.SplitLayoutEngine.prototype */ {
      __name: "SplitLayoutEngine",
      /**
       * registered children widgets
       * @type {classes.WidgetBase[]}
       * @protected
       */
      _registeredWidgets: null,
      /**
       * stylesheet id
       * @protected
       */
      _styleSheetId: null,
      /**
       * @inheritDoc
       * @constructs
       */
      constructor: function (widget) {
        $super.constructor.call(this, widget);
        this._styleSheetId = "boxLayout_" + widget.getUniqueIdentifier();
        this._registeredWidgets = [];
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        for (let i = this._registeredWidgets.length - 1; i > -1; i--) {
          const wi = this._registeredWidgets[i];
          wi.destroy();
          this.unregisterChild(wi);
        }
        this._registeredWidgets.length = 0;
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      resetSizes: function () {
        $super.resetSizes.call(this);
        this._getLayoutInfo().setPreferred(0, 0);
      },

      /**
       * Refresh the child widgets layout
       */
      refreshLayout: function () {
        const session = context.SessionService.getCurrent();
        let app = session && session.getCurrentApplication();
        if (app && app.layout) {
          app.scheduler.layoutCommand({
            resize: true
          });
        }
      },

      /**
       * @inheritDoc
       */
      registerChild: function (widget) {
        if (!(widget instanceof cls.SplitterWidget)) {
          if (this._registeredWidgets.indexOf(widget) === -1) {
            this._registeredWidgets.push(widget);
          }
        }
      },

      /**
       * @inheritDoc
       */
      unregisterChild: function (widget) {
        cls.ArrayHelper.remove(this._registeredWidgets, widget);
      },

      /**
       * @inheritDoc
       */
      adjustMeasure: function () {
        const layoutInfo = this._getLayoutInfo();
        let measureX = 0,
          measureY = 0,
          minX = 0,
          minY = 0,
          maxX = 0,
          maxY = 0;
        const decorateX = layoutInfo.getDecorating().getWidth(),
          decorateY = layoutInfo.getDecorating().getHeight();
        let preferredX = 0,
          preferredY = 0;

        const children = this._getWidget().getChildren();
        for (const childWidget of children) {
          if (!childWidget.isVisible()) {
            continue;
          }
          const widgetInfo = this._getLayoutInfo(childWidget);
          measureX = Math.max(measureX, widgetInfo.getMeasured().getWidth());
          measureY = Math.max(measureY, widgetInfo.getMeasured().getHeight());
          minX = Math.max(minX, widgetInfo.getMinimal().getWidth());
          minY = Math.max(minY, widgetInfo.getMinimal().getHeight());
          maxX = Math.max(maxX, widgetInfo.getMaximal().getWidth());
          maxY = Math.max(maxY, widgetInfo.getMaximal().getHeight());
          preferredX = Math.max(preferredX, widgetInfo.getPreferred().getWidth());
          preferredY = Math.max(preferredY, widgetInfo.getPreferred().getHeight());
        }

        layoutInfo.setMeasured(measureX + decorateX, measureY + decorateY);
        layoutInfo.setPreferred(Math.max(preferredX, measureX + decorateX), Math.max(preferredY, measureY + decorateY));
        layoutInfo.setMinimal(minX + decorateX, minY + decorateY);
        layoutInfo.setMaximal(maxX + decorateX, maxY + decorateY);
      },

      /**
       * @inheritDoc
       */
      adjustStretchability: function () {
        const layoutInfo = this._getLayoutInfo(),
          children = this._widget.getChildren();
        for (const element of children) {
          const widgetInfo = this._getLayoutInfo(element);
          if (widgetInfo.isXStretched() || widgetInfo.isChildrenXStretched()) {
            layoutInfo.addChildrenStretchX(widgetInfo);
          }
          if (widgetInfo.isYStretched() || widgetInfo.isChildrenYStretched()) {
            layoutInfo.addChildrenStretchY(widgetInfo);
          }
        }
      },

      /**
       * @inheritDoc
       */
      prepareApplyLayout: function () {
        const layoutInfo = this._getLayoutInfo(),
          decorateX = layoutInfo.getDecorating().getWidth(),
          decorateY = layoutInfo.getDecorating().getHeight(),
          minx = Math.max(layoutInfo.getAvailable().getWidth(), layoutInfo.getMinimal().getWidth()),
          miny = Math.max(layoutInfo.getAvailable().getHeight(), layoutInfo.getMinimal().getHeight());
        for (const childWidget of this._getWidget().getChildren()) {
          if (!childWidget.isVisible()) {
            continue;
          }
          const widgetInfo = this._getLayoutInfo(childWidget);
          widgetInfo.setAvailable(minx - decorateX, miny - decorateY);
          widgetInfo.setAllocated(minx - decorateX, miny - decorateY);
        }
        layoutInfo.setAllocated(minx, miny);
        this._styleRules[".g_measured #w_" + this._widget.getUniqueIdentifier() + ".g_measureable"] = {
          height: layoutInfo.getAllocated().getHeight() + "px",
          width: layoutInfo.getAllocated().getWidth() + "px"
        };
        this._styleRules[".g_measured #w_" + this._widget.getUniqueIdentifier() + ".g_measureable>div>.swipeable.containerElement"] = {
          height: layoutInfo.getAllocated().getHeight() - decorateY + "px",
          width: layoutInfo.getAllocated().getWidth() - decorateX + "px"
        };
      },

      /**
       * @inheritDoc
       */
      applyLayout: function () {
        context.styler.appendStyleSheet(this._styleRules, this._styleSheetId, true, this.getLayoutSheetId());
      }
    };
  });
});
